import { useMediaQuery } from '@vueuse/core';
import { onScopeDispose, ref, watch, type CSSProperties, type Ref } from 'vue';

/** Share of the pie the orbiting slice takes. */
export const ORBIT_FRACTION = 0.08;
/** One turn of the orbit. */
export const ORBIT_TURN_MS = 2000;
/** The dash's glide between two progress values (see the pie's styles). */
export const WEDGE_GLIDE_MS = 600;
/** The angle at which the wedge starts at 12 o'clock. */
export const REST_DEG = -90;
// t + t² - t³: unit speed at the start, at rest at the end; the bezier is
// the same curve, for the dash's transition.
const settleEase = (t: number) => t + t * t - t * t * t;
const SETTLE_EASING = 'cubic-bezier(0.333, 0.333, 0.667, 1)';

export type SettleStyle = Pick<CSSProperties, 'transitionDuration' | 'transitionTimingFunction'>;

/**
 * The turn of the pie's wedge while nothing is measured yet (from the click
 * until the first percent): a small slice of the pie orbiting it, clockwise
 * from 12. At the first percent it settles: it turns on to 12 while its dash
 * morphs to the progress over the same span, starting at the orbit's speed
 * and coming to rest at 12 (the same ease on both, so they arrive together).
 * A run cut short meanwhile drops it to rest at once.
 *
 * Returns the wedge's rotation in degrees from 12 o'clock, to write into its
 * transform attribute, and the inline transition that stretches and eases
 * the dash's glide to the turn while settling. Both the orbit and the settle
 * write the rotation from a frame loop: a CSS or Web Animation on the
 * transform left Chrome not painting the wedge once it was cancelled, and the
 * attribute always repaints. With reduced motion the slice stands still at 12
 * instead of orbiting, and the settle is just the dash's glide.
 *
 * @param orbiting whether the slice should be turning
 * @param measuring whether a run is still under way when the orbit ends; the
 *   wedge settles then, and rests at once otherwise
 */
export function useOrbitingWedge(orbiting: Ref<boolean>, measuring: Ref<boolean>) {
  const reducedMotion = useMediaQuery('(prefers-reduced-motion: reduce)');
  const angle = ref(REST_DEG);
  const settleStyle = ref<SettleStyle>();
  let frame: number | undefined;
  let settleTimer: ReturnType<typeof setTimeout> | undefined;

  function stopTurning() {
    if (frame !== undefined) {
      cancelAnimationFrame(frame);
      frame = undefined;
    }
    clearTimeout(settleTimer);
  }

  function rest() {
    stopTurning();
    angle.value = REST_DEG;
    settleStyle.value = undefined;
  }

  function orbit() {
    rest();
    if (reducedMotion.value) {
      return;
    }
    const start = performance.now();
    const tick = (now: number) => {
      angle.value = REST_DEG + (((now - start) % ORBIT_TURN_MS) / ORBIT_TURN_MS) * 360;
      frame = requestAnimationFrame(tick);
    };
    frame = requestAnimationFrame(tick);
  }

  function settle() {
    stopTurning();
    const from = angle.value;
    const remaining = REST_DEG + 360 - from;
    if (from === REST_DEG) {
      return;
    }
    const duration = Math.round(Math.max(WEDGE_GLIDE_MS, (remaining / 360) * ORBIT_TURN_MS));
    settleStyle.value = {
      transitionDuration: `${duration}ms`,
      transitionTimingFunction: SETTLE_EASING,
    };
    const start = performance.now();
    const tick = (now: number) => {
      const t = Math.min(1, (now - start) / duration);
      if (t < 1) {
        angle.value = from + remaining * settleEase(t);
        frame = requestAnimationFrame(tick);
        return;
      }
      frame = undefined;
      angle.value = REST_DEG;
      // The dash's transition ends with the turn; a moment's grace before the
      // plain timing returns, so it is not cut short by a late frame.
      settleTimer = setTimeout(() => (settleStyle.value = undefined), 100);
    };
    frame = requestAnimationFrame(tick);
  }

  watch(
    orbiting,
    (now, before) => {
      if (now) {
        orbit();
      } else if (before && measuring.value) {
        settle();
      } else {
        rest();
      }
    },
    { immediate: true },
  );
  onScopeDispose(rest);

  return { angle, settleStyle };
}
